import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  CreditCard, 
  Smartphone, 
  Plus, 
  Search, 
  Filter,
  Eye,
  Edit,
  Trash2,
  Copy,
  Lock,
  Unlock,
  AlertCircle,
  CheckCircle,
  Clock,
  TrendingUp,
  TrendingDown,
  Users,
  FileText,
  HardDrive,
  Server,
  Monitor,
  Printer,
  Upload,
  Download
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { passwordManagementService } from '../../lib/passwordManagementService';

interface PasswordItem {
  id: string;
  name: string;
  item_type: string;
  category: string;
  strength?: 'weak' | 'medium' | 'strong';
  favorite: boolean;
  last_used?: string;
  created_at: string;
}

interface Device {
  id: string;
  name: string;
  type: string;
  model: string;
  serial_number: string;
  purchase_date: string;
  warranty_info?: string;
  notes?: string;
  backup_status: string;
  last_backup?: string;
}

interface Subscription {
  id: string;
  name: string;
  cost: number;
  billing_cycle: string;
  next_billing: string;
  status: 'active' | 'cancelled' | 'expired';
  category: string;
  notes?: string;
}

interface DigitalAsset {
  id: string;
  name: string;
  type: string;
  url?: string;
  username?: string;
  notes?: string;
  deputies: string[];
  instructions?: string;
}

interface DigitalOverviewStats {
  totalPasswords: number;
  weakPasswords: number;
  totalDevices: number;
  backedUpDevices: number;
  totalSubscriptions: number;
  monthlyCost: number;
  totalAssets: number;
  securityScore: number;
}

export function MobileDigitalOverview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'passwords' | 'devices' | 'subscriptions' | 'assets'>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Handle navigation to specific sections
  const handleNavigateToSection = (section: string) => {
    navigate(`/mobile/digital/${section}`);
  };

  // Handle button actions
  const handleViewPassword = (passwordId: string) => {
    // Navigate to password details or show modal
    console.log('View password:', passwordId);
  };

  const handleCopyPassword = (passwordId: string) => {
    // Copy password to clipboard
    console.log('Copy password:', passwordId);
  };

  const handleEditPassword = (passwordId: string) => {
    // Navigate to edit password
    console.log('Edit password:', passwordId);
  };

  const handleEditDevice = (deviceId: string) => {
    // Navigate to edit device
    console.log('Edit device:', deviceId);
  };

  const handleEditSubscription = (subscriptionId: string) => {
    // Navigate to edit subscription
    console.log('Edit subscription:', subscriptionId);
  };

  const handleViewAsset = (assetId: string) => {
    // Navigate to view asset
    console.log('View asset:', assetId);
  };

  const handleEditAsset = (assetId: string) => {
    // Navigate to edit asset
    console.log('Edit asset:', assetId);
  };

  const handleAddNew = () => {
    // Show add new modal or navigate to add form
    console.log('Add new item');
  };

  // Data states
  const [passwords, setPasswords] = useState<PasswordItem[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [digitalAssets, setDigitalAssets] = useState<DigitalAsset[]>([]);
  const [stats, setStats] = useState<DigitalOverviewStats>({
    totalPasswords: 0,
    weakPasswords: 0,
    totalDevices: 0,
    backedUpDevices: 0,
    totalSubscriptions: 0,
    monthlyCost: 0,
    totalAssets: 0,
    securityScore: 0
  });

  // Load data from database
  useEffect(() => {
    const loadData = async () => {
      if (!user) return;

      try {
        setLoading(true);
        setError(null);

        // Load passwords
        const { data: passwordVaults } = await supabase
          .from('password_vaults')
          .select('id')
          .eq('user_id', user.id);

        if (passwordVaults && passwordVaults.length > 0) {
          const vaultId = passwordVaults[0].id;
          const passwordItems = await passwordManagementService.getVaultItems(vaultId);
          setPasswords(passwordItems.map(item => ({
            id: item.id,
            name: item.name,
            item_type: item.item_type,
            category: item.category || 'general',
            strength: item.strength,
            favorite: item.favorite,
            last_used: item.last_used,
            created_at: item.created_at
          })));
        }

        // Load devices
        const { data: devicesData } = await supabase
          .from('devices')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        setDevices(devicesData || []);

        // Load subscriptions (mock data for now - would need subscription table)
        const mockSubscriptions: Subscription[] = [
          {
            id: '1',
            name: 'Netflix',
            cost: 15.99,
            billing_cycle: 'monthly',
            next_billing: '2024-02-15',
            status: 'active',
            category: 'entertainment'
          },
          {
            id: '2',
            name: 'Spotify Premium',
            cost: 9.99,
            billing_cycle: 'monthly',
            next_billing: '2024-02-10',
            status: 'active',
            category: 'music'
          },
          {
            id: '3',
            name: 'Adobe Creative Suite',
            cost: 52.99,
            billing_cycle: 'monthly',
            next_billing: '2024-02-20',
            status: 'active',
            category: 'software'
          }
        ];
        setSubscriptions(mockSubscriptions);

        // Load digital assets
        const { data: assetsData } = await supabase
          .from('digital_assets')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        setDigitalAssets(assetsData || []);

        // Calculate stats
        const weakPasswords = passwords.filter(p => p.strength === 'weak').length;
        const backedUpDevices = devices.filter(d => d.backup_status === 'backed_up').length;
        const monthlyCost = subscriptions.reduce((sum, sub) => sum + sub.cost, 0);
        const securityScore = Math.max(0, 100 - (weakPasswords * 10) - (devices.length - backedUpDevices) * 5);

        setStats({
          totalPasswords: passwords.length,
          weakPasswords,
          totalDevices: devices.length,
          backedUpDevices,
          totalSubscriptions: subscriptions.length,
          monthlyCost,
          totalAssets: digitalAssets.length,
          securityScore
        });

      } catch (err) {
        console.error('Error loading digital estate data:', err);
        setError('Failed to load digital estate data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'backed_up':
        return 'text-green-600 bg-green-100';
      case 'cancelled':
      case 'not_backed_up':
        return 'text-red-600 bg-red-100';
      case 'expired':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStrengthColor = (strength?: string) => {
    switch (strength) {
      case 'strong':
        return 'text-green-600 bg-green-100';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100';
      case 'weak':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  const renderOverview = () => (
    <div className="space-y-4">
      {/* Security Score */}
      <Card className="p-4 bg-gradient-to-r from-idecide-primary to-idecide-primary-hover">
        <div className="text-center text-white">
          <h3 className="text-lg font-semibold mb-2">Digital Security Score</h3>
          <div className="text-3xl font-bold mb-2">{stats.securityScore}/100</div>
          <div className="w-full bg-white bg-opacity-20 rounded-full h-2">
            <div 
              className="bg-white h-2 rounded-full transition-all duration-300"
              style={{ width: `${stats.securityScore}%` }}
            />
          </div>
        </div>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Lock size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Passwords</p>
              <p className="text-lg font-semibold">{stats.totalPasswords}</p>
              {stats.weakPasswords > 0 && (
                <p className="text-xs text-red-600">{stats.weakPasswords} weak</p>
              )}
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Smartphone size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Devices</p>
              <p className="text-lg font-semibold">{stats.totalDevices}</p>
              <p className="text-xs text-green-600">{stats.backedUpDevices} backed up</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CreditCard size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Subscriptions</p>
              <p className="text-lg font-semibold">{stats.totalSubscriptions}</p>
              <p className="text-xs text-gray-600">{formatCurrency(stats.monthlyCost)}/month</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Shield size={20} className="text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Assets</p>
              <p className="text-lg font-semibold">{stats.totalAssets}</p>
              <p className="text-xs text-gray-600">Digital assets</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="p-4">
        <h3 className="text-lg font-semibold mb-3">Recent Activity</h3>
        <div className="space-y-2">
          {passwords.slice(0, 3).map((password) => (
            <div key={password.id} className="flex items-center gap-3 p-2 rounded bg-gray-50">
              <Lock size={16} className="text-blue-500" />
              <div className="flex-1">
                <p className="text-sm font-medium">{password.name}</p>
                <p className="text-xs text-gray-500">{password.category}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs ${getStrengthColor(password.strength)}`}>
                {password.strength || 'unknown'}
              </span>
            </div>
          ))}
        </div>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        <Button 
          variant="outline" 
          className="w-full"
          onClick={() => handleNavigateToSection('security')}
        >
          <Lock size={16} className="mr-2" />
          Manage Passwords
        </Button>
        <Button 
          variant="outline" 
          className="w-full"
          onClick={() => handleNavigateToSection('devices')}
        >
          <Smartphone size={16} className="mr-2" />
          Manage Devices
        </Button>
      </div>
    </div>
  );

  const renderPasswords = () => (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search passwords..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
        />
      </div>

      {/* Password List */}
      <div className="space-y-3">
        {passwords
          .filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
          .map((password) => (
            <Card key={password.id} className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Lock size={16} className="text-blue-500" />
                    <h3 className="font-medium text-gray-900">{password.name}</h3>
                    {password.favorite && <span className="text-yellow-500">★</span>}
                  </div>
                  <p className="text-sm text-gray-600">{password.category}</p>
                  <p className="text-xs text-gray-500">{password.item_type}</p>
                </div>
                <div className="flex gap-1">
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => handleViewPassword(password.id)}
                  >
                    <Eye size={14} />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => handleCopyPassword(password.id)}
                  >
                    <Copy size={14} />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => handleEditPassword(password.id)}
                  >
                    <Edit size={14} />
                  </Button>
                </div>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className={`px-2 py-1 rounded-full text-xs ${getStrengthColor(password.strength)}`}>
                  {password.strength || 'unknown'}
                </span>
                {password.last_used && (
                  <span className="text-xs text-gray-500">
                    Last used: {new Date(password.last_used).toLocaleDateString()}
                  </span>
                )}
              </div>
            </Card>
          ))}
      </div>
    </div>
  );

  const renderDevices = () => (
    <div className="space-y-4">
      {/* Device List */}
      <div className="space-y-3">
        {devices.map((device) => (
          <Card key={device.id} className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Smartphone size={16} className="text-purple-500" />
                  <h3 className="font-medium text-gray-900">{device.name}</h3>
                </div>
                <p className="text-sm text-gray-600">{device.type} • {device.model}</p>
                <p className="text-xs text-gray-500">Serial: {device.serial_number}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(device.backup_status)}`}>
                  {device.backup_status}
                </span>
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => handleEditDevice(device.id)}
                >
                  <Edit size={14} />
                </Button>
              </div>
            </div>
            {device.last_backup && (
              <p className="text-xs text-gray-500 mt-2">
                Last backup: {new Date(device.last_backup).toLocaleDateString()}
              </p>
            )}
          </Card>
        ))}
      </div>
    </div>
  );

  const renderSubscriptions = () => (
    <div className="space-y-4">
      {/* Subscription List */}
      <div className="space-y-3">
        {subscriptions.map((subscription) => (
          <Card key={subscription.id} className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard size={16} className="text-green-500" />
                  <h3 className="font-medium text-gray-900">{subscription.name}</h3>
                </div>
                <p className="text-sm text-gray-600">{formatCurrency(subscription.cost)}/{subscription.billing_cycle}</p>
                <p className="text-xs text-gray-500">Next billing: {subscription.next_billing}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(subscription.status)}`}>
                  {subscription.status}
                </span>
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => handleEditSubscription(subscription.id)}
                >
                  <Edit size={14} />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Monthly Cost Summary */}
      <Card className="p-4 bg-gray-50">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Total Monthly Cost</span>
          <span className="text-lg font-semibold text-gray-900">
            {formatCurrency(stats.monthlyCost)}
          </span>
        </div>
      </Card>
    </div>
  );

  const renderAssets = () => (
    <div className="space-y-4">
      {/* Digital Assets List */}
      <div className="space-y-3">
        {digitalAssets.map((asset) => (
          <Card key={asset.id} className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Shield size={16} className="text-orange-500" />
                  <h3 className="font-medium text-gray-900">{asset.name}</h3>
                </div>
                <p className="text-sm text-gray-600">{asset.type}</p>
                {asset.username && (
                  <p className="text-xs text-gray-500">Username: {asset.username}</p>
                )}
              </div>
              <div className="flex gap-1">
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => handleViewAsset(asset.id)}
                >
                  <Eye size={14} />
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => handleEditAsset(asset.id)}
                >
                  <Edit size={14} />
                </Button>
              </div>
            </div>
            {asset.deputies && asset.deputies.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-gray-500">Deputies: {asset.deputies.join(', ')}</p>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-4 pb-safe space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded"></div>
          <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 pb-safe space-y-4">
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertCircle size={16} className="text-red-500" />
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-safe space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Digital Estate</h2>
          <p className="text-sm text-gray-600">Manage passwords, devices, and digital assets</p>
        </div>
        <Button size="sm" onClick={handleAddNew}>
          <Plus size={16} className="mr-2" />
          Add
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 overflow-x-auto">
        <Button
          variant={activeTab === 'overview' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('overview')}
          className="whitespace-nowrap"
        >
          Overview
        </Button>
        <Button
          variant={activeTab === 'passwords' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('passwords')}
          className="whitespace-nowrap"
        >
          Passwords
        </Button>
        <Button
          variant={activeTab === 'devices' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('devices')}
          className="whitespace-nowrap"
        >
          Devices
        </Button>
        <Button
          variant={activeTab === 'subscriptions' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('subscriptions')}
          className="whitespace-nowrap"
        >
          Subscriptions
        </Button>
        <Button
          variant={activeTab === 'assets' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('assets')}
          className="whitespace-nowrap"
        >
          Assets
        </Button>
      </div>

      {/* Content */}
      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'passwords' && renderPasswords()}
      {activeTab === 'devices' && renderDevices()}
      {activeTab === 'subscriptions' && renderSubscriptions()}
      {activeTab === 'assets' && renderAssets()}
    </div>
  );
} 